import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Icon from "../components/Icon";
import { Button, Field } from "../components/ui";
import PhotoButton from "../components/PhotoButton";
import VoiceNote from "../components/VoiceNote";
import SignOff from "../components/SignOff";
import { useAuth } from "../auth";
import { apiGet } from "../api";
import { submitQueued } from "../offline";
import { getSticky, setSticky } from "../sticky";
import { WORK_TYPES } from "../constants";
import { C, R, S } from "../theme";

const label = { color: C.sub, fontSize: 13, fontWeight: 700, marginBottom: S.sm, textTransform: "uppercase" };
const cardDivider = { height: 1, background: C.border, margin: `${S.lg}px 0` };
const ta = { background: C.card2, borderRadius: R.md, border: `1px solid ${C.borderStrong}`, color: C.text, fontSize: 16, padding: S.md, minHeight: 80, width: "100%", outline: "none", marginBottom: S.sm };

export default function MaintenanceNew() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const aid = params.get("attraction_id");
  const [attraction, setAttraction] = useState(null);
  const [workDate, setWorkDate] = useState(new Date().toISOString().slice(0, 10));
  const [workType, setWorkType] = useState(params.get("prefill_type") || "routine");
  const [desc, setDesc] = useState(params.get("prefill_desc") || "");
  const [tech, setTech] = useState("");
  const [component, setComponent] = useState("");
  const [defect, setDefect] = useState("");
  const [parts, setParts] = useState("");
  const [retest, setRetest] = useState("");
  const [loc, setLoc] = useState(getSticky("location"));
  const [photo, setPhoto] = useState(null);
  const [beforePhoto, setBeforePhoto] = useState(null);
  const [afterPhoto, setAfterPhoto] = useState(null);
  const [signoff, setSignoff] = useState({ signature: null, name: user?.display_name || user?.company_name || "", initials: "", complete: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { if (aid) apiGet(`/attractions/${aid}`).then(setAttraction).catch(() => {}); }, [aid]);

  const save = async () => {
    setErr("");
    if (!desc.trim() || !tech.trim()) { setErr("Description and technician name are required"); return; }
    if (!signoff.complete) { setErr("Signature, printed name and initials are all required"); return; }
    setBusy(true);
    try {
      if (loc) setSticky("location", loc);
      await submitQueued(`/attractions/${aid}/maintenance`, {
        work_date: workDate, work_type: workType, description: desc, technician_name: tech,
        component: component || null, defect_description: defect || null, parts_replaced: parts || null,
        retest_outcome: retest || null, location: loc || null,
        photo, before_photo: beforePhoto, after_photo: afterPhoto,
        signature: signoff.signature, signed_by_name: signoff.name, signed_by_initials: signoff.initials,
      });
      nav(-1);
    } catch (e) { setErr(e?.message || "Save failed"); } finally { setBusy(false); }
  };

  const retestBtn = (key, txt, color) => (
    <button data-testid={`maint-retest-${key || "none"}`} onClick={() => setRetest(key)} style={{ flex: 1, background: retest === key ? color : C.card2, borderRadius: R.md, padding: `${S.md}px 0`, color: retest === key ? (key === "fail" ? C.text : C.black) : C.text, fontWeight: 800, border: `1px solid ${C.borderStrong}` }}>{txt}</button>
  );

  return (
    <div className="fade-up">
      <div style={{ display: "flex", alignItems: "center", gap: S.sm, padding: S.lg }}>
        <button className="pressable" onClick={() => nav(-1)}><Icon name="chevron-back" size={28} color={C.text} /></button>
        <div className="display" style={{ color: C.text, fontSize: 20, fontWeight: 900 }}>Maintenance / Repair</div>
      </div>
      <div style={{ padding: S.lg }}>
        {attraction && <div style={{ color: C.muted, marginBottom: S.md }}>{attraction.name}</div>}
        <Field label="Work date" testID="maint-date" type="date" value={workDate} onChange={(e) => setWorkDate(e.target.value)} />
        <div style={label}>Work type</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: S.sm, marginBottom: S.md }}>
          {WORK_TYPES.map((w) => <button key={w.key} data-testid={`work-${w.key}`} onClick={() => setWorkType(w.key)} style={{ background: workType === w.key ? C.amber : C.card2, borderRadius: R.md, padding: `${S.md}px ${S.md}px`, color: workType === w.key ? C.black : C.text, fontWeight: 700, border: `1px solid ${C.borderStrong}` }}>{w.label}</button>)}
        </div>
        <Field label="Component / part (optional)" testID="maint-component" value={component} onChange={(e) => setComponent(e.target.value)} placeholder="e.g. Hydraulic ram, blower unit" />
        <div style={label}>Defect / fault found (optional)</div>
        <textarea data-testid="maint-defect" value={defect} onChange={(e) => setDefect(e.target.value)} placeholder="Describe the fault or reason for the work" style={ta} />
        <div style={label}>Work carried out / description *</div>
        <textarea data-testid="maint-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What work was carried out?" style={ta} />
        <VoiceNote onText={(t) => setDesc((d) => (d + " " + t).trim())} />
        <div style={{ height: S.md }} />
        <Field label="Parts replaced (optional)" testID="maint-parts" value={parts} onChange={(e) => setParts(e.target.value)} placeholder="e.g. 2x M12 bolts, blower motor" />
        <Field label="Technician name" testID="maint-tech" value={tech} onChange={(e) => setTech(e.target.value)} placeholder="Who did the work?" />
        <Field label="Location / site (optional)" testID="maint-location" value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="e.g. North field, Pitch 3" />
        <div style={label}>Post-repair retest outcome (optional)</div>
        <div style={{ display: "flex", gap: S.sm, marginBottom: S.md }}>
          {retestBtn("", "Not tested", C.card2)}
          {retestBtn("pass", "Retest PASS", C.green)}
          {retestBtn("fail", "Retest FAIL", C.red)}
        </div>
        <div style={label}>Before photo (optional)</div>
        <PhotoButton value={beforePhoto} onCapture={setBeforePhoto} allowGallery testID="maint-before-photo" />
        <div style={{ height: S.md }} />
        <div style={label}>After photo (optional)</div>
        <PhotoButton value={afterPhoto} onCapture={setAfterPhoto} allowGallery testID="maint-after-photo" />
        <div style={{ height: S.md }} />
        <div style={label}>Additional photo (optional)</div>
        <PhotoButton value={photo} onCapture={setPhoto} allowGallery testID="maint-photo" />
        <div style={cardDivider} />
        <SignOff defaultName={signoff.name} onChange={setSignoff} />
        <div style={{ height: S.md }} />
        {!!err && <div style={{ color: C.red, fontWeight: 700, marginBottom: S.sm }}>{err}</div>}
        <Button title="Save Maintenance Log" testID="maint-save" onPress={save} loading={busy} />
        <div style={{ height: S.xxl }} />
      </div>
    </div>
  );
}
